import React, { useContext, useEffect, useState } from 'react';
import { DeckContext } from '../context/DeckContext';
import {
	Box,
	Button,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	InputLabel,
	Select,
	MenuItem,
	Divider,
	Stack,
} from '@mui/material';
import BlockType from '../../../../../enums/blockType';
import {
	MIN_STAIR_VIABLE_BLOCK_HEIGHT,
	MIN_STAIR_WIDTH,
} from '../../../../../constants/limits';
import { Block, Dimensions, Edge } from '../../../../../types/shared/blocks';
import EdgeLabel from '../../../../../enums/edgeLabel';
import EdgeType from '../../../../../enums/edgeType';
import getEdgeFromString from '../../../../../utils/getEdge';
import getDefautlStair, {
	getDefaultStairV2,
} from '../utils/getDefaultStair.ts';
import { toastError } from '../../../../../Toaster/Toast';
import { getOppositeEdge } from '../utils/edgeUtils.js';
import getDefaultLanding, {
	getDefaultLandingV2,
} from '../utils/getDefaultLanding.ts';

const Sidebar: React.FC = () => {
	const { state, dispatch, transformWrapperRef, onAutoSave } =
		useContext(DeckContext);
	const [openDialog, setOpenDialog] = useState(false);
	const [dialogType, setDialogType] = useState(null);
	const [selectedBlockId, setSelectedBlockId] = useState<string | null>();
	const [selectedBlockEdge, setSelectedBlockEdge] =
		useState<EdgeLabel | null>();
	const [disableAddStair, setDisableAddStair] = useState(false);

	const handleOpenDialog = (type) => {
		setDialogType(type);
		setOpenDialog(true);
	};

	const deckboxes = state.blocks.filter((blk) => blk.type == BlockType.DECKBOX);
	const landings = deckboxes.flatMap((dk) => dk.landings);
	const bumpouts = deckboxes.flatMap((dk) => dk.bumpouts);
	const stairs = deckboxes.flatMap((dk) => dk.stairs);

	const options =
		dialogType == BlockType.STAIR ? [...deckboxes, ...landings] : deckboxes;

	const parentBlock = [...deckboxes, ...landings, ...stairs, ...bumpouts].find(
		(blk) => blk?.id === selectedBlockId
	);

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setDialogType(null);
		setSelectedBlockId(null);
		setSelectedBlockEdge(null);
		setDisableAddStair(false);
	};

	const handleAddAssociation = () => {
		if (!selectedBlockId) {
			toastError({
				title: 'Select a block type',
				description: `Please select a ${dialogType == BlockType.STAIR ? 'Landing/DeckBox' : 'DeckBox'}.`,
			});

			return;
		}

		const parentBlock = [
			...deckboxes,
			...landings,
			...bumpouts,
			...stairs,
		].find((blk) => blk?.id === selectedBlockId);

		// Check for validation before proceeding
		if (
			(dialogType === 'Stair' ||
				(dialogType === 'Landing' &&
					parentBlock?.type === BlockType.DECKBOX)) &&
			parentBlock &&
			parentBlock.dimensions.height <= MIN_STAIR_VIABLE_BLOCK_HEIGHT
		) {
			setDisableAddStair(true);
			return; // Don't proceed with adding
		}

		if (dialogType === 'Bumpout') {
			const defaultBumpout: Block = {
				type: BlockType.BUMPOUT,
				// Position will be set in the reducer
				dimensions: {
					width: 84,
					depth: 24,
					height: parentBlock?.dimensions.height || 0,
				},
				displayDimensions: { width: 80, height: 20 },
				parentBlock: {
					id: parentBlock?.id,
					type: parentBlock?.type,
				},
				edges: [
					{ label: EdgeLabel.TOP, type: EdgeType.HOUSE, length: 84 },
					{ label: EdgeLabel.RIGHT, type: EdgeType.DECKING, length: 24 },
					{ label: EdgeLabel.BOTTOM, type: EdgeType.DECKING, length: 84 },
					{ label: EdgeLabel.LEFT, type: EdgeType.DECKING, length: 24 },
				],
			};

			dispatch({
				type: 'ADD_BUMPOUT',
				payload: defaultBumpout,
			});

			if (onAutoSave) {
				onAutoSave();
			}

			handleCloseDialog();
		} else if (dialogType === 'Stair') {
			if (parentBlock && selectedBlockEdge) {
				const blockEdge = parentBlock.edges.find(
					(ed) => ed.label == selectedBlockEdge
				);

				const stairData = getDefautlStair(parentBlock, blockEdge);

				if (stairData?.defaultStair)
					dispatch({
						type: 'ADD_STAIR',
						payload: stairData?.defaultStair,
					});

				if (stairData?.addIntermediateLanding) {
					const landing = getDefaultLandingV2({
						parentBlockId: stairData.defaultStair.id,
						parentBlockType: BlockType.STAIR,
						height: stairData.defaultStair.dimensions.endHeight,
						parentBlockDisplayHeight:
							stairData.defaultStair.displayDimensions.height,
						parentBlockDisplayWidth:
							stairData.defaultStair.displayDimensions.width,
						blockEdge: blockEdge,
					});

					if (landing) {
						dispatch({
							type: 'ADD_LANDING',
							payload: landing,
						});

						const secondStair = getDefaultStairV2({
							parentBlockId: landing.id,
							parentBlockType: BlockType.LANDING,
							startHeight: landing.dimensions.height,
							parentBlockDisplayHeight: landing.displayDimensions.height,
							parentBlockDisplayWidth: landing.displayDimensions.width,
							blockEdge: blockEdge,
						});

						if (secondStair)
							dispatch({
								type: 'ADD_STAIR',
								payload: secondStair,
							});
					}
				}
				handleCloseDialog();
			}
		} else if (dialogType === 'Landing') {
			if (parentBlock && selectedBlockEdge) {
				const blockEdge = parentBlock.edges.find(
					(ed) => ed.label == selectedBlockEdge
				);
				const defaultLanding = getDefaultLanding(parentBlock, blockEdge);

				dispatch({
					type: 'ADD_LANDING',
					payload: defaultLanding,
				});
				handleCloseDialog();
			}
		}
	};

	const addBlock = (type) => {
		let defaultDimensions: Dimensions = {
			width: 48,
			depth: 48,
			height: 0,
		};

		const defaultDeckBoxDimensions: Dimensions = {
			width: 120,
			depth: 120,
			height: 0,
		};

		const defaultDeckBoxEdges: Edge[] = [
			{ label: EdgeLabel.TOP, type: EdgeType.HOUSE_ATTACHED, length: 120 },
			{ label: EdgeLabel.RIGHT, type: EdgeType.RAILING, length: 120 },
			{ label: EdgeLabel.BOTTOM, type: EdgeType.RAILING, length: 120 },
			{ label: EdgeLabel.LEFT, type: EdgeType.RAILING, length: 120 },
		];

		if (type === 'DeckBox') {
			defaultDimensions = defaultDeckBoxDimensions;
		}

		let edges: Edge[] = [];

		if (type === 'DeckBox') {
			edges = defaultDeckBoxEdges;
		}

		let position = undefined;
		if (type === 'DeckBox') {
			// Start deckbox at a fixed position instead of centering
			position = {
				x: 80,
				y: 150,
			};
		}

		const newBlock: Block = {
			type: type,
			// Position is handled by the reducer
			rotation: 0,
			dimensions: defaultDimensions,
			displayDimensions: {
				width: defaultDimensions.width,
				height: defaultDimensions.depth,
			},
			edges,
			parentBlock: {
				id: null,
				type: null,
			},
		};

		dispatch({ type: 'ADD_BLOCK', payload: newBlock });
	};

	useEffect(() => {
		// Re-enable the button if any DeckBox is above the limit
		const anyDeckBoxAboveLimit = state.blocks
			.filter((blk) => blk.type === 'DeckBox')
			.some(
				(deckbox) => deckbox.dimensions.height > MIN_STAIR_VIABLE_BLOCK_HEIGHT
			);
		if (anyDeckBoxAboveLimit) {
			setDisableAddStair(false);
		}
	}, [state.blocks]);

	return (
		<Box
			sx={{
				width: 200,
				padding: 2,
				borderRight: '1px solid #ccc',
				boxSizing: 'border-box',
				backgroundColor: 'white',
			}}
		>
			<Typography variant="h6">Blocks</Typography>
			<Box
				sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
			>
				<Button variant="contained" onClick={() => addBlock(BlockType.DECKBOX)}>
					Add DeckBox
				</Button>
				<Button
					variant="contained"
					onClick={() => handleOpenDialog(BlockType.LANDING)}
				>
					Add Landing
				</Button>
				<Button
					variant="contained"
					onClick={() => handleOpenDialog(BlockType.STAIR)}
					disabled={disableAddStair}
				>
					Add Stair
				</Button>
				<Button
					variant="contained"
					onClick={() => handleOpenDialog(BlockType.BUMPOUT)}
				>
					Add Bumpout
				</Button>
			</Box>

			{/* Dialog for associating Stair/Landing or Bumpout to a DeckBox */}
			<Dialog open={openDialog} onClose={handleCloseDialog}>
				<DialogTitle>
					{dialogType === BlockType.STAIR
						? 'Add Stair to Landing/DeckBox'
						: dialogType == BlockType.LANDING
							? 'Add Landing to DeckBox'
							: 'Add Bumpout to DeckBox'}
				</DialogTitle>

				<DialogContent>
					{state.blocks.filter((blk) => blk.type === 'DeckBox').length === 0 ? (
						<Typography>
							No DeckBoxes available. Please add a DeckBox first.
						</Typography>
					) : (
						<Stack sx={{ marginTop: 2 }}>
							<InputLabel>
								Select {dialogType == BlockType.STAIR ? 'Landing/' : ''}DeckBox
							</InputLabel>
							<Select
								value={selectedBlockId}
								variant="outlined"
								onChange={(e) => setSelectedBlockId(e.target.value)}
								fullWidth
							>
								{options.map((block) => (
									<MenuItem key={block.id} value={block.id}>
										{block.label}
									</MenuItem>
								))}
							</Select>
						</Stack>
					)}

					{(dialogType === 'Stair' || dialogType == 'Landing') &&
						selectedBlockId && (
							<Stack sx={{ marginTop: 2 }}>
								<InputLabel>Select Edge</InputLabel>
								<Select
									value={selectedBlockEdge}
									variant="outlined"
									onChange={(e) => {
										setSelectedBlockEdge(getEdgeFromString(e.target.value));
									}}
									fullWidth
								>
									{['Top', 'Right', 'Bottom', 'Left'].map((side) => (
										<MenuItem key={side} value={side}>
											{side}
										</MenuItem>
									))}
								</Select>
							</Stack>
						)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Cancel</Button>
					<Button
						onClick={handleAddAssociation}
						disabled={
							state.blocks.filter((blk) => blk.type === 'DeckBox').length ===
								0 ||
							!selectedBlockId ||
							(dialogType !== BlockType.BUMPOUT && !selectedBlockEdge)
						}
						variant="contained"
					>
						Add
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={
					(dialogType === 'Stair' ||
						(dialogType === 'Landing' &&
							parentBlock?.type == BlockType.DECKBOX)) &&
					openDialog &&
					Boolean(selectedBlockId) &&
					Boolean(
						(() => {
							const parentBlock = [...deckboxes, ...landings].find(
								(blk) => blk.id === selectedBlockId
							);

							return parentBlock && parentBlock.dimensions.height <= 15.25;
						})()
					)
				}
				onClose={handleCloseDialog}
			>
				<DialogTitle>Invalid DeckBox Height</DialogTitle>
				<Divider />
				<DialogContent>
					<Typography>
						You can&apos;t add {dialogType === 'Stair' ? 'stairs' : 'landing'}{' '}
						to a Deckbox with a height less than or equal to
						{MIN_STAIR_VIABLE_BLOCK_HEIGHT} inches tall.
					</Typography>
				</DialogContent>
				<Divider />
				<DialogActions>
					<Button
						onClick={handleCloseDialog}
						variant="contained"
						sx={{ textTransform: 'none' }}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
export default Sidebar;
